import {
  IRemitHeadlineCell,
  RemitAccuracyOutcome,
  RemitAiAgreement,
  RemitConfidenceBand,
  RemitPickKind,
  RemitScoredBenefit,
} from "types/RemitAccuracy/RemitAccuracy";

/**
 * CLE-2818 — every label and number format on the Remit accuracy tab, in one place so the tests can
 * pin them.
 */

export const REMIT_BENEFIT_LABELS: Record<RemitScoredBenefit, string> = {
  COINSURANCE_PERCENT: "Co-insurance",
  FLAT_COPAY: "Copay",
  PROFESSIONAL_COPAY: "Physician copay",
};

export const REMIT_KIND_LABELS: Record<RemitPickKind, string> = {
  REPLAY: "Today's config (re-run nightly)",
  QUOTED: "At time of check",
};

export const REMIT_OUTCOME_LABELS: Record<RemitAccuracyOutcome, string> = {
  MATCH: "Match",
  ENGINE_DIFFERENT: "Miss",
  ENGINE_ZERO: "Miss · engine $0",
  ENGINE_NO_VALUE: "Miss",
  NOT_CONFIGURED: "Not configured",
  REMITS_DISAGREE: "Remits disagree",
};

export interface IRemitEmptyContext {
  /** When the nightly job last wrote anything; null = it has never run. */
  lastUpdatedAt: string | null;
}

/**
 * Why a view shows nothing, told truthfully: the nightly job has never run, or it has and nothing matches
 * the filters. ("At time of check" is hidden until it has scored data, so it needs no wording of its own.)
 */
export const emptyStateText = (context: IRemitEmptyContext) =>
  context.lastUpdatedAt
    ? "No remit-confirmed checks for these filters"
    : "Waiting for the first nightly run";

/** "At time of check" is shown only once at least one of its picks has been scored against a remit. */
export const quotedIsAvailable = (quoted?: { scoredRows: number }) =>
  (quoted?.scoredRows ?? 0) > 0;

/** "Last updated 2 hours ago". */
export const relativeTime = (iso: string, now: Date = new Date()) => {
  const minutes = Math.max(
    0,
    Math.round((now.getTime() - new Date(iso).getTime()) / 60000)
  );
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
};

/** The remit share a not-configured count is about: a copay for the copays, co-insurance otherwise. */
export const remitShareWording = (benefit: RemitScoredBenefit) =>
  benefit === "COINSURANCE_PERCENT" ? "with co-insurance" : "with a copay";

export const READ_FROM_LABELS: Record<string, string> = {
  LINE: "Line",
  SUMMARY: "Service-line summary",
  INDEX: "Earlier check (history)",
  FORCED: "Fixed payer rule",
  OVERRIDE: "Payer override",
  NONE: "Nothing",
};

export const REMIT_EXCLUSION_LABELS: Record<string, string> = {
  NO_PRIMARY_CHECK: "Estimate has no primary eligibility check",
  CLAIM_PAYER_UNRESOLVED: "Payer not resolved on the claim",
  CLAIM_IS_ORDER_SECONDARY_PAYER: "Claim is the estimate's secondary payer",
  PAYER_DIFFERENT: "Different payer from the estimate",
  REPROCESS_OR_REVERSAL_HISTORY: "Reprocessed or reversed",
  SECONDARY_EVENT: "Has a secondary-payer remit",
  LOW_ORDER_MATCH_CONFIDENCE: "Weak claim → estimate match (< 0.9)",
  DIFFERENT_PLAN_YEAR: "Check in a different plan year",
  NO_LINE_ITEMS: "Remit has no service lines",
};

export const exclusionLabel = (reason: string) =>
  REMIT_EXCLUSION_LABELS[reason] ?? reason;

export const percentOf = (cell: Pick<IRemitHeadlineCell, "scored" | "match">) =>
  cell.scored === 0 ? null : (100 * cell.match) / cell.scored;

/** "95.0% (799 / 841)" — a percentage is never shown without its sample size. */
export const accuracyText = (
  cell?: Pick<IRemitHeadlineCell, "scored" | "match">
) => {
  if (!cell || cell.scored === 0) return "not measured";
  return `${(percentOf(cell) as number).toFixed(
    1
  )}% (${cell.match.toLocaleString()} / ${cell.scored.toLocaleString()})`;
};

/** A row of the by-account or by-payer table. */
interface IRemitRow {
  cells: IRemitHeadlineCell[];
}

/** One benefit's accuracy for a row, or null when nothing was scored for it. */
export const rowAccuracy = (
  row: IRemitRow,
  kind: RemitPickKind,
  benefit: RemitScoredBenefit
) => {
  const cell = row.cells.find(
    (each) => each.kind === kind && each.benefit === benefit
  );
  return cell ? percentOf(cell) : null;
};

/** At least one benefit scored for the kind. A row with nothing scored is left out of the tables. */
export const hasMeasurement = (row: IRemitRow, kind: RemitPickKind) =>
  row.cells.some((cell) => cell.kind === kind && cell.scored > 0);

export const withMeasurement = <T extends IRemitRow>(
  rows: T[],
  kind: RemitPickKind
): T[] => rows.filter((row) => hasMeasurement(row, kind));

/**
 * Sorts a by-account or by-payer table. `field` is the name field or a benefit; a benefit sorts by its
 * accuracy percentage, and a row with no measurement for that benefit goes last whichever way the column
 * is sorted. Ties fall back to the name, ascending.
 */
export const sortRemitRows = <T extends IRemitRow>(
  rows: T[],
  field: string,
  order: number,
  kind: RemitPickKind,
  name: (row: T) => string
): T[] => {
  const byName = (a: T, b: T) => name(a).localeCompare(name(b));
  const benefit = field as RemitScoredBenefit;
  if (!(benefit in REMIT_BENEFIT_LABELS))
    return [...rows].sort((a, b) => order * byName(a, b));
  return [...rows].sort((a, b) => {
    const left = rowAccuracy(a, kind, benefit);
    const right = rowAccuracy(b, kind, benefit);
    if (left === null || right === null) {
      if (left !== right) return left === null ? 1 : -1;
      return byName(a, b);
    }
    return order * (left - right) || byName(a, b);
  });
};

/** A check in pVerify's portal, by its RequestID. */
export const pverifyRequestUrl = (requestId: string) =>
  `https://premium.pverify.com/EligibilityInquirySummary/EligibilityInquirySummary/${encodeURIComponent(
    requestId
  )}`;

/**
 * Reasons the tab does not show. NO_PRIMARY_CHECK — the estimate never had a pVerify check, so there was
 * nothing to score — is still stored and returned by the API; it is hidden as noise.
 */
export const HIDDEN_EXCLUSION_REASONS = ["NO_PRIMARY_CHECK"];

/** The "Remits not scored" counts without the hidden reasons, in the "X of Y" and the cards alike. */
export const visibleExclusions = (exclusions?: {
  remits: number;
  notScored: number;
  reasons: { reason: string; count: number }[];
}) => {
  const all = exclusions ?? { remits: 0, notScored: 0, reasons: [] };
  const hidden = all.reasons
    .filter((each) => HIDDEN_EXCLUSION_REASONS.includes(each.reason))
    .reduce((sum, each) => sum + each.count, 0);
  return {
    remits: all.remits - hidden,
    notScored: all.notScored - hidden,
    reasons: all.reasons.filter(
      (each) => !HIDDEN_EXCLUSION_REASONS.includes(each.reason)
    ),
  };
};

export const formatRemitValue = (
  value: number | null | undefined,
  benefit: RemitScoredBenefit
) => {
  if (value === null || value === undefined) return "—";
  if (benefit === "COINSURANCE_PERCENT") return `${value}%`;
  return Number.isInteger(value)
    ? `$${value.toLocaleString("en-US")}`
    : `$${value.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
};

/** "$150 – $1,850", "$0", or "" when there is no value at all (the caller renders "no value", muted). */
export const formatRange = (
  min: number | null,
  max: number | null,
  benefit: RemitScoredBenefit
) => {
  if (min === null || max === null) return "";
  return min === max
    ? formatRemitValue(min, benefit)
    : `${formatRemitValue(min, benefit)} – ${formatRemitValue(max, benefit)}`;
};

const iso = (date: Date) => date.toISOString().slice(0, 10);

export type DateOfServicePreset =
  | "ALL"
  | "LAST_90_DAYS"
  | "LAST_12_MONTHS"
  | "THIS_YEAR"
  | "LAST_YEAR";

export const DATE_OF_SERVICE_OPTIONS: {
  value: DateOfServicePreset;
  label: string;
}[] = [
  { value: "ALL", label: "All time" },
  { value: "LAST_90_DAYS", label: "Last 90 days" },
  { value: "LAST_12_MONTHS", label: "Last 12 months" },
  { value: "THIS_YEAR", label: "This calendar year" },
  { value: "LAST_YEAR", label: "Last calendar year" },
];

export const dateOfServiceRange = (
  preset: DateOfServicePreset,
  today: Date = new Date()
): { dosFrom?: string; dosTo?: string } => {
  const year = today.getUTCFullYear();
  switch (preset) {
    case "LAST_90_DAYS":
      return { dosFrom: iso(new Date(today.getTime() - 90 * 86400000)) };
    case "LAST_12_MONTHS": {
      const from = new Date(today);
      from.setUTCFullYear(year - 1);
      return { dosFrom: iso(from) };
    }
    case "THIS_YEAR":
      return { dosFrom: `${year}-01-01`, dosTo: `${year}-12-31` };
    case "LAST_YEAR":
      return { dosFrom: `${year - 1}-01-01`, dosTo: `${year - 1}-12-31` };
    default:
      return {};
  }
};

export const lineLabel = (line: {
  message: string[];
  planCoverageDescription: string | null;
  serviceName: string;
}) =>
  line.message.length
    ? line.message.join(" / ")
    : line.planCoverageDescription ?? line.serviceName;

/** CLE-2820 — the AI comparison. The UI only ever says "AI". */
export const AI_AGREEMENT_LABELS: Record<RemitAiAgreement, string> = {
  BOTH_RIGHT: "Both right",
  ONLY_AI: "Only AI right",
  ONLY_ENGINE: "Only engine right",
  BOTH_WRONG: "Both wrong",
};

export const AI_AGREEMENT_ORDER: RemitAiAgreement[] = [
  "BOTH_RIGHT",
  "ONLY_AI",
  "ONLY_ENGINE",
  "BOTH_WRONG",
];

export const CONFIDENCE_BAND_LABELS: Record<RemitConfidenceBand, string> = {
  HIGH: "0.9 – 1",
  MEDIUM: "0.7 – 0.9",
  LOW: "Below 0.7",
};

export const AI_SKIP_REASON_LABELS: Record<string, string> = {
  TOO_LARGE: "Response too large to send",
  NO_CANDIDATES: "No benefit values to choose from",
  IDENTIFIER_FOUND: "Held back: identifier-like text found",
};

/** `IDENTIFIER_FOUND:DATE` reads as the identifier label plus the pattern. */
export const aiSkipReasonLabel = (reason: string) => {
  const [code, detail] = reason.split(":");
  const label = AI_SKIP_REASON_LABELS[code] ?? code;
  return detail
    ? `${label} (${detail.toLowerCase().replace(/_/g, " ")})`
    : label;
};

/** "0.93" — two decimals, or a dash. */
export const formatConfidence = (confidence: number | null | undefined) =>
  confidence === null || confidence === undefined ? "—" : confidence.toFixed(2);
